/**
 * The call surface: a pre-join step, then the meeting itself, both in one
 * dialog.
 *
 * A meeting is not a notification strip. Joining one is a decision — is my
 * camera on, is the right microphone picked, does the browser even let me,
 * and who is in there already — and that decision belongs in front of you
 * before anyone's phone rings, not in a corner afterwards. Every way in goes
 * through it: starting one, answering a ring, pressing Join on the bar.
 *
 * See _design/sketches/Meetings.md for the copy and the measurements.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { createPortal } from 'react-dom'

import type { Call, UUID } from '@/api/types'
import {
  classifyMediaError,
  mediaErrorMessage,
  missingDevices,
  openLocalMedia,
  pagePolicy,
  type MediaErrorKind,
} from '@/hooks/callMedia'
import type { CallPreferences, CallSessionState, StartShareOptions } from '@/hooks/useCallSession'
import { Button, Checkbox, Modal, Select } from '@/components/ui'
import { InCallStage } from '@/components/CallStage'
import { ringCountdown } from '@/lib/callCopy'
import { participantName } from '@/components/CallStageView'
import styles from './CallWindow.module.css'

export type CallStage =
  | {
      kind: 'prejoin'
      title: string
      media: 'audio' | 'video'
      call?: Call | null
      context?: string
      /** Set when you are in this call on another device (calls-v1 §5.1 rule 5). */
      elsewhere?: string | null
    }
  | { kind: 'in-call'; title: string; call: Call }

export type CallSessionApi = CallSessionState & {
  setMicEnabled: (on: boolean) => void
  setCameraEnabled: (on: boolean) => void
  startShare: (options?: StartShareOptions) => Promise<void>
  stopShare: () => Promise<void>
  dismissShareError: () => void
  retryPeer: (userId: UUID) => void
  retryDevices?: () => void
}

interface CallWindowProps {
  stage: CallStage | null
  selfUserId?: UUID
  session: CallSessionApi
  preferences: CallPreferences
  onPreferencesChange: (preferences: CallPreferences) => void
  joining: boolean
  onJoin: () => void
  onCancel: () => void
  onHangUp: () => void
  /**
   * Put the meeting away without leaving it. Closing the window is not
   * hanging up — the only way out of a call is Leave call.
   */
  onMinimise?: () => void
  onPopOut?: () => void
  onDock?: () => void
  portalTarget?: HTMLElement
  /** The server implements calls-v2. Without it, sharing is absent, not disabled. */
  screenSharingEnabled?: boolean
  /** Go to the room's own timeline; the meeting carries on under the pill. */
  onChat?: () => void
  /** "Share a screen as I join" was ticked for the meeting on the stage. */
  sharePrompt?: boolean
  onSharePromptDone?: () => void
}

/**
 * The preview is the point of the pre-join step: it is the only way to find
 * out that the browser said no, or that the camera is pointing at the ceiling,
 * before other people are involved.
 */
function useLocalPreview(
  active: boolean,
  camera: boolean,
  audioDeviceId: string | undefined,
  videoDeviceId: string | undefined,
  onForget: (forgot: { audio: boolean; video: boolean }) => void,
): {
  stream: MediaStream | null
  error: MediaErrorKind | null
  devices: MediaDeviceInfo[]
  retry: () => void
} {
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [error, setError] = useState<MediaErrorKind | null>(null)
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [attempt, setAttempt] = useState(0)

  const onForgetRef = useRef(onForget)
  onForgetRef.current = onForget

  useEffect(() => {
    if (!active) {
      setStream(null)
      setError(null)
      return undefined
    }

    let cancelled = false
    let opened: MediaStream | null = null

    void openLocalMedia(
      (constraints) => navigator.mediaDevices.getUserMedia(constraints),
      { audioDeviceId, videoDeviceId, video: camera },
      // Audio only is the person's decision here, offered with the reason.
      false,
    )
      .then((media) => {
        if (cancelled) {
          media.stream.getTracks().forEach((track) => track.stop())
          return
        }
        opened = media.stream
        setStream(media.stream)
        setError(null)
        if (media.forgot.audio || media.forgot.video) onForgetRef.current(media.forgot)
        // Device names are only readable once a stream is open; before that
        // the browser lists unlabelled entries nobody can choose between.
        if (typeof navigator.mediaDevices.enumerateDevices === 'function') {
          void navigator.mediaDevices
            .enumerateDevices()
            .then((list) => {
              if (cancelled) return
              setDevices(list)
              // A remembered device the browser no longer lists is forgotten,
              // not asked for again every time the dialog opens.
              const missing = missingDevices(list, { audioDeviceId, videoDeviceId })
              if (missing.audio || missing.video) onForgetRef.current(missing)
            })
            .catch(() => undefined)
        }
      })
      .catch((caught: unknown) => {
        if (cancelled) return
        setStream(null)
        setError(classifyMediaError(caught, pagePolicy(), camera))
      })

    return () => {
      cancelled = true
      opened?.getTracks().forEach((track) => track.stop())
    }
  }, [active, attempt, audioDeviceId, camera, videoDeviceId])

  const retry = useCallback(() => setAttempt((count) => count + 1), [])

  return { stream, error, devices, retry }
}

function PreviewVideo({ stream }: { stream: MediaStream }): ReactElement {
  const ref = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    if (ref.current !== null) ref.current.srcObject = stream
  }, [stream])

  return <video ref={ref} className={styles.previewVideo} autoPlay playsInline muted />
}

/** Who is already in, and what they are doing — the line that decides it. */
function joinedNames(call: Call | null | undefined, selfUserId: UUID | undefined): string[] {
  if (call === null || call === undefined) return []
  return call.participants
    .filter((participant) => participant.status === 'joined' && participant.user?.id !== selfUserId)
    .map((participant) => {
      const person = participant.user
      if (person === null || person === undefined) return 'Someone'
      const display = person.display_name.trim()
      return display === '' ? person.username : display
    })
}

export function listNames(names: string[]): string {
  if (names.length === 0) return ''
  if (names.length === 1) return names[0] ?? ''
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  if (names.length === 3) return `${names[0]}, ${names[1]} and ${names[2]}`
  return `${names[0]} and ${names.length - 1} more`
}

export function CallWindow({
  stage,
  selfUserId,
  session,
  preferences,
  onPreferencesChange,
  joining,
  onJoin,
  onCancel,
  onHangUp,
  onMinimise,
  onPopOut,
  onDock,
  portalTarget,
  screenSharingEnabled = false,
  onChat,
  sharePrompt,
  onSharePromptDone,
}: CallWindowProps): ReactElement | null {
  const joinRef = useRef<HTMLButtonElement>(null)
  const prejoin = stage?.kind === 'prejoin'
  const cameraOffered = stage?.kind === 'prejoin' && stage.media === 'video'

  const preview = useLocalPreview(
    prejoin,
    cameraOffered && preferences.camera,
    preferences.audioDeviceId,
    preferences.videoDeviceId,
    (forgot) =>
      onPreferencesChange({
        ...preferences,
        audioDeviceId: forgot.audio ? undefined : preferences.audioDeviceId,
        videoDeviceId: forgot.video ? undefined : preferences.videoDeviceId,
      }),
  )

  const microphones = useMemo(
    () => preview.devices.filter((device) => device.kind === 'audioinput'),
    [preview.devices],
  )
  const cameras = useMemo(() => preview.devices.filter((device) => device.kind === 'videoinput'), [preview.devices])

  const call = stage === null ? null : stage.kind === 'in-call' ? stage.call : (stage.call ?? null)

  // A meeting that is already three people deep should not be joined by a
  // fourth microphone nobody asked for.
  const alreadyIn = joinedNames(call, selfUserId)
  const busyRoom = alreadyIn.length >= 3
  useEffect(() => {
    if (prejoin && busyRoom && preferences.mic) {
      onPreferencesChange({ ...preferences, mic: false })
    }
    // Only when the dialog opens on a busy call; afterwards the person decides.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busyRoom, prejoin])

  if (stage === null) return null

  if (stage.kind === 'prejoin') {
    const sharingName =
      call !== null && call.sharing_user_id != null ? participantName(call, call.sharing_user_id) : null

    return (
      <Modal
        open
        onClose={onCancel}
        title={stage.title}
        description={stage.context}
        size="md"
        tone="stage"
        closeOnOverlayClick={false}
        // Join is what the dialog is for. Starting on the microphone toggle
        // meant Enter switched the microphone off instead of going in.
        initialFocusRef={joinRef}
        footer={
          <>
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button ref={joinRef} variant="primary" loading={joining} onClick={onJoin}>
              Join
            </Button>
          </>
        }
      >
        <div className={styles.prejoin}>
          <div className={styles.preview}>
            {cameraOffered && preferences.camera && preview.stream !== null ? (
              <PreviewVideo stream={preview.stream} />
            ) : (
              // Camera off is said once, by the line underneath.
              <p className={styles.placeholder}>{cameraOffered && preferences.camera ? 'Opening the camera…' : ''}</p>
            )}
            {/* The one sentence people want confirmed before anyone sees them. */}
            {!cameraOffered || !preferences.camera ? (
              <span className={styles.previewState}>
                <span className={styles.previewDot} />
                Camera off · nobody sees you yet
              </span>
            ) : null}
          </div>

          {/* Joining here moves the meeting off the other device. That is
              worth knowing before pressing Join, not after. */}
          {stage.elsewhere === null || stage.elsewhere === undefined ? null : (
            <div className={styles.notice} role="status">
              <span className={styles.noticeText}>{stage.elsewhere}</span>
            </div>
          )}

          {/* Whichever went wrong first: the devices refusing to open, or the
              server refusing the call. A device failure says what to do about
              it, and every one of them can be tried again. */}
          {preview.error !== null ? (
            <div className={styles.notice} role="alert">
              <span className={styles.noticeText}>
                {mediaErrorMessage(preview.error, cameraOffered && preferences.camera)}
              </span>
              {preview.error === 'missing' && cameraOffered && preferences.camera ? (
                <Button size="sm" variant="secondary" onClick={() => onPreferencesChange({ ...preferences, camera: false })}>
                  Audio only
                </Button>
              ) : null}
              <Button size="sm" variant="ghost" onClick={preview.retry}>
                Try again
              </Button>
            </div>
          ) : session.error === null ? null : (
            <p className={styles.error} role="alert">
              {session.error}
            </p>
          )}

          {alreadyIn.length === 0 ? null : (
            <div className={styles.roster}>
              <div className={styles.rosterText}>
                <span className={styles.rosterLine}>
                  {listNames(alreadyIn)} {alreadyIn.length === 1 ? 'is' : 'are'} in
                </span>
                <span className={styles.rosterMeta}>
                  {sharingName === null ? '' : `${sharingName} is sharing a screen · `}
                  {alreadyIn.length + 1} of 8 seats
                </span>
              </div>
            </div>
          )}

          <div className={styles.toggles}>
            <Button
              size="sm"
              variant={preferences.mic ? 'secondary' : 'ghost'}
              aria-pressed={preferences.mic}
              onClick={() => onPreferencesChange({ ...preferences, mic: !preferences.mic })}
            >
              {preferences.mic ? 'Microphone on' : 'Microphone off'}
            </Button>
            {cameraOffered ? (
              <Button
                size="sm"
                variant={preferences.camera ? 'secondary' : 'ghost'}
                aria-pressed={preferences.camera}
                onClick={() => onPreferencesChange({ ...preferences, camera: !preferences.camera })}
              >
                {preferences.camera ? 'Camera on' : 'Camera off'}
              </Button>
            ) : null}
          </div>

          {/* Which microphone and camera, once the browser lets us name them.
              A laptop with a headset plugged in has two of each, and the
              default is the wrong one often enough to ask. */}
          {microphones.length > 1 || cameras.length > 1 ? (
            <div className={styles.devices}>
              {microphones.length > 1 ? (
                <Select
                  label="Microphone"
                  value={preferences.audioDeviceId ?? ''}
                  onChange={(event) =>
                    onPreferencesChange({
                      ...preferences,
                      audioDeviceId: event.target.value === '' ? undefined : event.target.value,
                    })
                  }
                  options={[
                    { value: '', label: 'Default microphone' },
                    ...microphones.map((device, index) => ({
                      value: device.deviceId,
                      label: device.label.trim() === '' ? `Microphone ${index + 1}` : device.label,
                    })),
                  ]}
                />
              ) : null}
              {cameraOffered && cameras.length > 1 ? (
                <Select
                  label="Camera"
                  value={preferences.videoDeviceId ?? ''}
                  onChange={(event) =>
                    onPreferencesChange({
                      ...preferences,
                      videoDeviceId: event.target.value === '' ? undefined : event.target.value,
                    })
                  }
                  options={[
                    { value: '', label: 'Default camera' },
                    ...cameras.map((device, index) => ({
                      value: device.deviceId,
                      label: device.label.trim() === '' ? `Camera ${index + 1}` : device.label,
                    })),
                  ]}
                />
              ) : null}
            </div>
          ) : null}

          <div className={styles.options}>
            {screenSharingEnabled && session.share.supported ? (
              <Checkbox
                label="Share a screen as I join"
                hint="You pick what to share in the next step. Nothing goes out before you have seen it."
                checked={preferences.shareOnJoin === true}
                onChange={(event) => onPreferencesChange({ ...preferences, shareOnJoin: event.target.checked })}
              />
            ) : null}
            <Checkbox
              label="Join muted"
              hint={busyRoom ? 'Ticked because three or more are already in.' : undefined}
              checked={!preferences.mic}
              onChange={(event) => onPreferencesChange({ ...preferences, mic: !event.target.checked })}
            />
          </div>

          <p className={styles.reassurance}>
            This meeting runs directly between the machines. Nobody hears you until you press Join.
          </p>
        </div>
      </Modal>
    )
  }

  return (
    <InCallStage
      title={stage.title}
      call={stage.call}
      selfUserId={selfUserId}
      session={session}
      onHangUp={onHangUp}
      onMinimise={onMinimise}
      onPopOut={onPopOut}
      onDock={onDock}
      portalTarget={portalTarget}
      onChat={onChat}
      screenSharingEnabled={screenSharingEnabled}
      sharePrompt={sharePrompt}
      onSharePromptDone={onSharePromptDone}
    />
  )
}

/**
 * The prompt for a call someone else started. Three choices, not two: joining
 * without a camera is a decision people make at the door, not a setting they
 * go looking for afterwards.
 */
export function IncomingCallPrompt({
  call,
  where,
  busyIn,
  selfUserId,
  deadline = null,
  onAccept,
  onAcceptAudioOnly,
  onDecline,
  onReply,
}: {
  call: Call
  /** The room it is in, for the line under the name. */
  where?: string
  /** The meeting this would hang up on, when there is one. */
  busyIn?: string | null
  selfUserId?: UUID
  /** When this device stops ringing, for the countdown. */
  deadline?: number | null
  onAccept: () => void
  onAcceptAudioOnly: () => void
  onDecline: () => void
  /** Say no in words: decline, and go to the room to write. */
  onReply?: () => void
}): ReactElement {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (deadline === null) return undefined
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [deadline])
  const countdown = ringCountdown(deadline, now)
  const others = joinedNames(call, selfUserId)
  const initiator = call.initiator
  const name =
    initiator === null ? 'Someone' : initiator.display_name.trim() === '' ? initiator.username : initiator.display_name
  const kind = call.media === 'video' ? 'Video meeting' : 'Audio call'
  const busy = busyIn !== null && busyIn !== undefined && busyIn !== ''

  return (
    <div className={styles.banner} role="status" aria-live="assertive">
      <div className={styles.bannerHead}>
        <div className={styles.bannerText}>
          <span className={styles.bannerName}>{name}</span>
          <span className={styles.bannerWhere}>{where === undefined ? kind : `${kind} in ${where}`}</span>
          {others.length === 0 ? null : (
            <span className={styles.bannerWhere}>
              {listNames(others)} {others.length === 1 ? 'is' : 'are'} already in
            </span>
          )}
          {/* Not a live region of its own: a second counted aloud is noise. */}
          {countdown === null ? null : (
            <span className={styles.bannerWhere} aria-hidden="true">
              {countdown}
            </span>
          )}
        </div>
      </div>

      {busy ? (
        <p className={styles.bannerBusy}>You’re in a meeting in {busyIn}. Answering here hangs up there.</p>
      ) : null}

      <div className={styles.actions}>
        <Button size="sm" variant="ghost" onClick={onDecline}>
          Decline
        </Button>
        {call.media === 'video' ? (
          <Button size="sm" variant="secondary" onClick={onAcceptAudioOnly}>
            Audio only
          </Button>
        ) : null}
        <Button size="sm" variant="primary" onClick={onAccept}>
          {busy ? 'Leave and join' : 'Join'}
        </Button>
      </div>
      {onReply === undefined ? null : (
        <button type="button" className={styles.bannerReply} onClick={onReply}>
          Reply with a message
        </button>
      )}
    </div>
  )
}

/**
 * The corner a call's prompts, quiet lines and pill share, stacked in the
 * order they are given with the last at the bottom, so none covers another.
 */
export function CallCorner({ children, pill }: { children: ReactNode; pill?: ReactNode }): ReactElement {
  // On the body, above every dialog. Inside the app's own tree the ring sat
  // under any open modal: a sound, and a card nobody could press.
  return createPortal(
    <>
      <div className={styles.corner} data-with-pill={pill !== undefined && pill !== null}>
        {children}
      </div>
      {pill === undefined || pill === null ? null : <div className={styles.pillCorner}>{pill}</div>}
    </>,
    document.body,
  )
}

/**
 * What the others say, played from one place that lasts as long as the call.
 * It used to come out of the tiles' own video elements, so putting the stage
 * away, opening another pre-join, or a face falling off the rail muted
 * somebody without a word.
 */
export function CallAudio({
  streams,
  onBlocked,
}: {
  streams: MediaStream[]
  /** The browser refused to play without a press. */
  onBlocked: (retry: () => void) => void
}): ReactElement {
  const elementsRef = useRef(new Map<string, HTMLAudioElement>())
  const onBlockedRef = useRef(onBlocked)
  onBlockedRef.current = onBlocked

  const playAll = useCallback((): void => {
    for (const element of elementsRef.current.values()) {
      const started = element.play?.()
      if (started !== undefined) {
        started.catch((caught: unknown) => {
          if ((caught as { name?: string } | null)?.name === 'NotAllowedError') onBlockedRef.current(playAll)
        })
      }
    }
  }, [])

  const ids = streams.map((stream) => stream.id).join(' ')
  useEffect(() => {
    playAll()
  }, [ids, playAll])

  return (
    <div hidden>
      {streams.map((stream) => (
        <audio
          key={stream.id}
          autoPlay
          ref={(element) => {
            if (element === null) {
              elementsRef.current.delete(stream.id)
              return
            }
            elementsRef.current.set(stream.id, element)
            if (element.srcObject !== stream) element.srcObject = stream
          }}
        />
      ))}
    </div>
  )
}

/**
 * A call's quiet state, in the corner: a meeting still running that you did
 * not answer, or what became of one you were in (calls-v1 §5.3a). No sound
 * and no deadline; it stays until it is acted on or put away.
 */
export function QuietCallBanner({
  text,
  action,
  onDismiss,
}: {
  text: string
  action?: { label: string; onClick: () => void }
  onDismiss: () => void
}): ReactElement {
  return (
    <div className={styles.banner} role="status" aria-live="polite">
      <div className={styles.bannerHead}>
        <span className={styles.bannerLine}>{text}</span>
        <button type="button" className={styles.bannerDismiss} aria-label="Dismiss" onClick={onDismiss}>
          <svg width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M5.4 5.4 14.6 14.6M14.6 5.4 5.4 14.6" />
          </svg>
        </button>
      </div>
      {action === undefined ? null : (
        <div className={styles.actions}>
          <Button size="sm" variant="primary" onClick={action.onClick}>
            {action.label}
          </Button>
        </div>
      )}
    </div>
  )
}
